import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const focusablesIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE));

/**
 * Keyboard focus for a hand-built modal dialog (`role="dialog"`, `aria-modal`).
 *
 * While the dialog is open:
 * - focus moves into it, to the first control after the header's close button
 *   (or the dialog itself, which should have tabIndex="-1");
 * - Tab and Shift+Tab wrap around inside it instead of reaching the page behind;
 * - when it closes, focus goes back to whatever opened it, if that is still on the
 *   page.
 *
 * Escape is deliberately not handled: it would throw away a half-filled form.
 *
 * @param {React.RefObject<HTMLElement>} dialogRef the outer dialog element
 */
const useDialogFocus = (dialogRef) => {
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const opener = document.activeElement;

    const start = focusablesIn(dialog).find((el) => !el.classList.contains('btn-close')) || dialog;
    start.focus();

    // On the document, not the dialog: a button that disables itself while saving
    // drops focus to <body>, and the next Tab must still come back inside.
    const onKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      // Recomputed each time, since buttons enable and disable as the form changes.
      const items = focusablesIn(dialog);
      const active = document.activeElement;
      const outside = !dialog.contains(active) || active === dialog;
      if (!items.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [dialogRef]);
};

export default useDialogFocus;
